import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, Check } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function ProjectAccess({ user, projects, isSelf, onSaved }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(user.projects || []);
  const [busy, setBusy] = useState(false);
  if (user.role === "admin") {
    return <span className="text-xs text-[#6B7280]" data-testid={`user-access-all-${user.id}`}>All projects (admin)</span>;
  }

  const toggle = (name) =>
    setSelected((s) => (s.includes(name) ? s.filter((n) => n !== name) : [...s, name]));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}/projects`, { projects: selected });
      toast.success(`Access updated for ${user.email}`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error("Could not update access", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {(user.projects || []).length === 0 ? (
          <span className="text-xs text-[#B91C1C]" data-testid={`user-access-none-${user.id}`}>No access</span>
        ) : (
          user.projects.map((p) => (
            <span key={p} className="text-[11px] font-heading font-semibold text-[#156F5B] bg-[#156F5B]/10 border border-[#156F5B]/20 rounded-full px-2 py-0.5">
              {p}
            </span>
          ))
        )}
        {!isSelf && (
          <button
            onClick={() => { setSelected(user.projects || []); setOpen(true); }}
            data-testid={`user-access-edit-${user.id}`}
            className="text-[11px] font-heading font-semibold text-[#156F5B] hover:underline"
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`user-access-form-${user.id}`}>
      <div className="flex flex-wrap gap-2">
        {projects.length === 0 && <span className="text-xs text-[#6B7280]">No connected projects yet</span>}
        {projects.map((p) => {
          const on = selected.includes(p.name);
          return (
            <button
              key={p.name}
              onClick={() => toggle(p.name)}
              data-testid={`user-access-option-${user.id}-${p.name}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-heading font-semibold ${
                on ? "bg-[#156F5B] text-white border-[#156F5B]" : "bg-white text-[#4D4D4D] border-[#E5E7EB] hover:border-[#156F5B]"
              }`}
              style={{ transition: "background-color .2s ease, color .2s ease, border-color .2s ease" }}
            >
              {on && <Check className="h-3 w-3" />}
              {p.dis || p.name}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid={`user-access-save-${user.id}`} className="k-btn !py-1 !px-3 !text-[11px]">
          Save access
        </button>
        <button onClick={() => setOpen(false)} className="text-[11px] text-[#6B7280] hover:text-[#212121]">
          Cancel
        </button>
      </div>
    </div>
  );
}

import { AddUserDialog } from "@/components/AddUserDialog";
import { PendingInvites } from "@/components/PendingInvites";
import { LastSeen } from "@/components/LastSeen";

export default function UsersPage() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const { data: users, loading } = useApiQuery(["users"], "/users");
  const { data: projectData } = useApiQuery(["projects"], "/projects");
  const projects = (projectData?.projects || []).filter((x) => x.connected);
  const reload = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const setRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success("Role updated");
      reload();
    } catch (e) {
      toast.error("Failed to update role", { description: e?.response?.data?.detail || e.message });
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      reload();
    } catch (e) {
      toast.error("Failed to delete", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="max-w-6xl fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Access</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">Users</h1>
          <p className="mt-3 text-sm text-[#4D4D4D]">
            Admins see the whole portfolio. Everyone else only sees the projects you grant them here.
            Accounts are created here by invitation — nobody can sign themselves up.
          </p>
        </div>
        <AddUserDialog
          projects={projects}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ["invites"] });
            reload();
          }}
        />
      </div>

      <PendingInvites />

      <div className="mt-8 k-card overflow-x-auto">
        <div className="grid grid-cols-12 min-w-[820px] px-6 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="col-span-3 k-sublabel">User</div>
          <div className="col-span-2 k-sublabel">Email</div>
          <div className="col-span-2 k-sublabel">Role</div>
          <div className="col-span-2 k-sublabel">Last sign-in</div>
          <div className="col-span-2 k-sublabel">Project access</div>
          <div className="col-span-1 k-sublabel text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 k-sublabel">Loading…</div>
        ) : (
          (users || []).map((u, i) => (
            <div key={u.id} className="grid grid-cols-12 min-w-[820px] items-center px-6 py-4 border-b border-[#F0F2F1] hover:bg-[#F9FAFB]" data-testid={`user-row-${i}`}>
              <div className="col-span-3 text-sm text-[#212121] font-heading font-semibold truncate">
                {u.name}
                {u.auth_provider === "google" && (
                  <span className="ml-2 text-[10px] font-heading font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-full px-1.5 py-0.5">
                    Google
                  </span>
                )}
              </div>
              <div className="col-span-2 text-sm text-[#4D4D4D] truncate">{u.email}</div>
              <div className="col-span-2">
                {u.id === me?.id ? (
                  <span className="inline-flex items-center rounded-full bg-[#156F5B]/10 px-2.5 py-0.5 text-[11px] font-heading font-semibold uppercase text-[#156F5B]">
                    {u.role} (you)
                  </span>
                ) : (
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger className="w-24 h-8 text-xs bg-white border-[#E5E7EB] text-[#212121]" data-testid={`user-role-select-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E5E7EB] text-[#212121]">
                      <SelectItem value="admin">admin</SelectItem>
                      <SelectItem value="user">user</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="col-span-2">
                <LastSeen iso={u.last_login_at} userId={u.id} />
              </div>
              <div className="col-span-2">
                <ProjectAccess user={u} projects={projects} isSelf={u.id === me?.id} onSaved={reload} />
              </div>
              <div className="col-span-1 text-right">
                {u.id !== me?.id && (
                  <button
                    onClick={() => del(u.id, u.email)}
                    data-testid={`user-delete-btn-${i}`}
                    className="p-1.5 rounded-md border border-[#E5E7EB] text-[#9CA3AF] hover:border-red-300 hover:text-red-600"
                    style={{ transition: "color .2s ease, border-color .2s ease" }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
